
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Mic, MicOff, Timer } from 'lucide-react';

export interface VoiceControlConfig {
  autoStopAfterSpeech: boolean;
  autoStopTimeout: number; // seconds
  stopAfterAIResponse: boolean;
  pushToTalkMode: boolean;
  continuousMode: boolean;
}

interface VoiceControlSettingsProps {
  config: VoiceControlConfig;
  onConfigChange: (config: VoiceControlConfig) => void;
  isVisible: boolean;
}

const VoiceControlSettings: React.FC<VoiceControlSettingsProps> = ({
  config,
  onConfigChange,
  isVisible
}) => {
  if (!isVisible) return null;

  const handleConfigUpdate = (key: keyof VoiceControlConfig, value: any) => {
    onConfigChange({
      ...config,
      [key]: value
    });
  };

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Configurações do Microfone
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Modo Contínuo vs Push-to-Talk */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Modo de Operação</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                checked={config.continuousMode}
                onCheckedChange={(checked) => {
                  handleConfigUpdate('continuousMode', checked);
                  if (checked) {
                    handleConfigUpdate('pushToTalkMode', false);
                  }
                }}
              />
              <Mic className="w-4 h-4" />
              <Label className="text-sm">Modo Contínuo (sempre ligado)</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={config.pushToTalkMode}
                onCheckedChange={(checked) => {
                  handleConfigUpdate('pushToTalkMode', checked);
                  if (checked) {
                    handleConfigUpdate('continuousMode', false);
                  }
                }}
              />
              <MicOff className="w-4 h-4" />
              <Label className="text-sm">Push-to-Talk (pressionar para falar)</Label>
            </div>
          </div>
        </div>

        {/* Auto-stop configurações */}
        {config.continuousMode && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Desligamento Automático</Label>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={config.autoStopAfterSpeech}
                onCheckedChange={(checked) => handleConfigUpdate('autoStopAfterSpeech', checked)}
              />
              <Timer className="w-4 h-4" />
              <Label className="text-sm">Desligar após silêncio</Label>
            </div>

            {config.autoStopAfterSpeech && (
              <div className="ml-6">
                <Label className="text-xs text-gray-600">Tempo de silêncio (segundos)</Label>
                <Select
                  value={config.autoStopTimeout.toString()}
                  onValueChange={(value) => handleConfigUpdate('autoStopTimeout', parseInt(value))}
                >
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2s</SelectItem>
                    <SelectItem value="3">3s</SelectItem>
                    <SelectItem value="5">5s</SelectItem>
                    <SelectItem value="10">10s</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                checked={config.stopAfterAIResponse}
                onCheckedChange={(checked) => handleConfigUpdate('stopAfterAIResponse', checked)}
              />
              <Label className="text-sm">Desligar após resposta da IA</Label>
            </div>
          </div>
        )}

        {/* Instruções */}
        <div className="text-xs text-gray-500 pt-2 border-t">
          {config.pushToTalkMode && (
            <p>• Mantenha pressionado o botão do microfone para falar</p>
          )}
          {config.continuousMode && config.autoStopAfterSpeech && (
            <p>• O microfone desligará automaticamente após {config.autoStopTimeout}s de silêncio</p>
          )}
          {config.continuousMode && config.stopAfterAIResponse && (
            <p>• O microfone desligará após cada resposta da IA</p>
          )}
          {config.continuousMode && !config.autoStopAfterSpeech && !config.stopAfterAIResponse && (
            <p>• O microfone permanecerá ligado até você desligá-lo manualmente</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VoiceControlSettings;
